import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Button } from '@mui/material';
import {
  DesktopWindows as DesktopIcon,
  Print as PrinterIcon,
  Phone as PhoneIcon,
  People as UsersIcon,
  Api as ApiIcon,
  Receipt as TaxIcon,
  Settings as SettingsGenericIcon,
  Tv as MonitorIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

interface ProgramTile {
  key: string;
  translationKey: string;
  fallbackLabel: string;
  icon: React.ReactNode;
  path: string | null;
}

const ProgramSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const tiles: ProgramTile[] = [
    {
      key: 'desktop_client',
      translationKey: 'settings.add_desktop_client',
      fallbackLabel: 'Add desktop client',
      icon: <DesktopIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'printer',
      translationKey: 'settings.printer_settings',
      fallbackLabel: 'Printer settings',
      icon: <PrinterIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'phone',
      translationKey: 'settings.phone_settings',
      fallbackLabel: 'Phone settings',
      icon: <PhoneIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'users',
      translationKey: 'settings.user_permissions',
      fallbackLabel: 'User / permissions',
      icon: <UsersIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: '/settings/program/users',
    },
    {
      key: 'api',
      translationKey: 'settings.api_interfaces',
      fallbackLabel: 'API / Interfaces / Order Smart',
      icon: <ApiIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'tax',
      translationKey: 'settings.tax_settings',
      fallbackLabel: 'MwSt / tax settings',
      icon: <TaxIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'general',
      translationKey: 'settings.general_settings',
      fallbackLabel: 'Settings',
      icon: <SettingsGenericIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
    {
      key: 'client_monitor',
      translationKey: 'settings.client_monitor',
      fallbackLabel: 'Setting up the client monitor',
      icon: <MonitorIcon sx={{ fontSize: 48, color: '#9ca3af' }} />,
      path: null,
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, pb: { xs: 4, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Back Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/settings')}
          sx={{
            color: '#e0e0e0 !important',
            backgroundColor: '#2a2d42 !important',
            borderRadius: '8px',
            px: 2.5,
            py: 1,
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#33364d !important',
            },
          }}
        >
          {t('settings.back') || 'Back'}
        </Button>
      </Box>

      <Grid container spacing={2}>
        {tiles.map((tile) => (
          <Grid item xs={6} sm={4} md={3} key={tile.key}>
            <Box
              onClick={() => {
                if (tile.path) navigate(tile.path);
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                py: 4,
                px: 2,
                borderRadius: '12px',
                backgroundColor: tile.path ? '#2a2d42 !important' : '#252838 !important',
                border: '1px solid #3a3d52',
                cursor: tile.path ? 'pointer' : 'default',
                opacity: tile.path ? 1 : 0.5,
                minHeight: 140,
                transition: 'all 0.15s ease',
                '&:hover': tile.path
                  ? {
                      backgroundColor: '#33364d !important',
                      border: '1px solid #00C896',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                    }
                  : {},
              }}
            >
              {tile.icon}
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  color: '#e0e0e0 !important',
                  textAlign: 'center',
                  lineHeight: 1.3,
                }}
              >
                {t(tile.translationKey) || tile.fallbackLabel}
              </Typography>
              {!tile.path && (
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    color: '#6b7280 !important',
                    fontStyle: 'italic',
                  }}
                >
                  {t('settings.coming_soon') || 'Coming soon'}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ProgramSettingsPage;
